import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchWorkAuthorizations, setFilters, clearFilters } from '@/store/slices/workAuthSlice';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import type { WorkAuthStatus, EmployeeType } from '@shared/schemas/workAuth';

const PAGE_SIZE = 10;

const WorkAuthListPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { workAuths, workAuthsLoading, pagination, filters } = useAppSelector(state => state.workAuth);

  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [statusFilter, setStatusFilter] = useState<WorkAuthStatus | 'all'>(filters.status || 'all');
  const [expiringFilter, setExpiringFilter] = useState<string>(
    filters.expiringWithinDays?.toString() || 'all'
  );

  useEffect(() => {
    // Load from URL params
    const expiring = searchParams.get('expiring');
    const search = searchParams.get('search');

    // Set search term from URL if present
    if (search) {
      setSearchTerm(search);
    }

    if (expiring || search) {
      if (expiring) {
        setExpiringFilter(expiring);
      }
      dispatch(setFilters({
        ...(expiring && { expiringWithinDays: parseInt(expiring) }),
        ...(search && { search })
      }));
      loadWorkAuthsWithParams(expiring, search);
    } else {
      loadWorkAuths();
    }
  }, [dispatch, searchParams]);

  const loadWorkAuths = () => {
    const params: any = {
      page: pagination.page,
      limit: PAGE_SIZE
    };

    if (searchTerm) params.search = searchTerm;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (expiringFilter !== 'all' && expiringFilter !== 'expired') {
      params.expiringWithinDays = parseInt(expiringFilter);
    } else if (expiringFilter === 'expired') {
      params.status = 'EXPIRED';
    }

    dispatch(fetchWorkAuthorizations(params));
  };

  const loadWorkAuthsWithFilter = (expiring: string) => {
    const params: any = {
      page: 1, // Reset to page 1 when filter changes
      limit: PAGE_SIZE
    };

    if (searchTerm) params.search = searchTerm;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (expiring !== 'all' && expiring !== 'expired') {
      params.expiringWithinDays = parseInt(expiring);
    } else if (expiring === 'expired') {
      params.status = 'EXPIRED';
    }

    dispatch(fetchWorkAuthorizations(params));
  };

  const loadWorkAuthsWithParams = (expiring: string | null, search: string | null) => {
    const params: any = {
      page: 1,
      limit: PAGE_SIZE
    };

    if (search) params.search = search;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (expiring && expiring !== 'all' && expiring !== 'expired') {
      params.expiringWithinDays = parseInt(expiring);
    } else if (expiring === 'expired') {
      params.status = 'EXPIRED';
    }

    dispatch(fetchWorkAuthorizations(params));
  };

  const handleSearch = () => {
    dispatch(setFilters({ search: searchTerm }));
    loadWorkAuths();
  };

  const handleFilterChange = () => {
    const newFilters: any = {};
    if (statusFilter !== 'all') newFilters.status = statusFilter;
    if (expiringFilter !== 'all') newFilters.expiringWithinDays = parseInt(expiringFilter);

    dispatch(setFilters(newFilters));
    loadWorkAuths();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setExpiringFilter('all');
    dispatch(clearFilters());
    setSearchParams({});
    loadWorkAuths();
  };

  const getStatusBadge = (status: WorkAuthStatus) => {
    const variants: Record<WorkAuthStatus, { variant: any; label: string }> = {
      ACTIVE: { variant: 'default', label: 'Active' },
      PENDING_RENEWAL: { variant: 'secondary', label: 'Pending Renewal' },
      DOCUMENTS_SUBMITTED: { variant: 'outline', label: 'Documents Submitted' },
      APPROVED: { variant: 'default', label: 'Approved' },
      EXPIRED: { variant: 'destructive', label: 'Expired' },
      TERMINATED: { variant: 'destructive', label: 'Terminated' }
    };

    const config = variants[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getDaysUntilExpiration = (expirationDate: Date) => {
    const today = new Date();
    const expiration = new Date(expirationDate);
    const diffTime = expiration.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpirationBadge = (days: number) => {
    if (days < 0) {
      return <Badge variant="destructive">Expired</Badge>;
    } else if (days <= 30) {
      return <Badge variant="destructive">{days} days</Badge>;
    } else if (days <= 60) {
      return <Badge variant="secondary">{days} days</Badge>;
    } else if (days <= 90) {
      return <Badge variant="outline">{days} days</Badge>;
    } else {
      return <Badge variant="outline">{days} days</Badge>;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Work Authorizations</h1>
        <p className="text-muted-foreground mt-1">View and manage all employee work authorizations</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search by name, ID, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="PENDING_RENEWAL">Pending Renewal</SelectItem>
                  <SelectItem value="DOCUMENTS_SUBMITTED">Documents Submitted</SelectItem>
                  <SelectItem value="EXPIRED">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Expiring Filter */}
            <div>
              <Select value={expiringFilter} onValueChange={setExpiringFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Expiring Within" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time Frames</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="60">60 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleFilterChange}>Apply Filters</Button>
            <Button variant="outline" onClick={handleClearFilters}>Clear Filters</Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>
              {pagination?.total || 0} Work Authorization{pagination?.total !== 1 ? 's' : ''}
            </CardTitle>
            <div className="text-sm text-muted-foreground">
              Page {pagination?.page || 1} of {pagination?.totalPages || 1}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {workAuthsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : !workAuths || workAuths.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No work authorizations found matching your filters
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr className="text-left text-sm text-muted-foreground">
                      <th className="pb-3 font-medium">Employee</th>
                      <th className="pb-3 font-medium">Employee ID / Form Code</th>
                      <th className="pb-3 font-medium">Document Type</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Expiration</th>
                      <th className="pb-3 font-medium">Days Left</th>
                      <th className="pb-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workAuths.map((workAuth) => {
                      const daysLeft = getDaysUntilExpiration(workAuth.expirationDate);
                      // Display employee ID, or if it's actually a form code in the employeeId field, show it
                      // The employeeId field may contain either an actual employee ID or form code
                      const identifier = workAuth.employee?.employeeId;
                      return (
                        <tr
                          key={workAuth.id}
                          className="border-b hover:bg-muted/50 cursor-pointer"
                          onClick={() => navigate(`/hr/work-auth/${workAuth.id}`)}
                        >
                          <td className="py-4">
                            <div>
                              <p className="font-medium">
                                {workAuth.employee?.firstName} {workAuth.employee?.lastName}
                              </p>
                              <p className="text-sm text-muted-foreground">{workAuth.employee?.email}</p>
                            </div>
                          </td>
                          <td className="py-4 text-sm">{identifier}</td>
                          <td className="py-4 text-sm">{workAuth.documentType}</td>
                          <td className="py-4">{getStatusBadge(workAuth.currentStatus)}</td>
                          <td className="py-4 text-sm">
                            {new Date(workAuth.expirationDate).toLocaleDateString()}
                          </td>
                          <td className="py-4">{getExpirationBadge(daysLeft)}</td>
                          <td className="py-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/hr/work-auth/${workAuth.id}`);
                              }}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-6">
                <div className="text-sm text-muted-foreground">
                  Showing {((pagination.page - 1) * PAGE_SIZE) + 1} to{' '}
                  {Math.min(pagination.page * PAGE_SIZE, pagination.total)} of {pagination.total}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => {
                      dispatch(fetchWorkAuthorizations({
                        ...filters,
                        page: pagination.page - 1,
                        limit: PAGE_SIZE
                      }));
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => {
                      dispatch(fetchWorkAuthorizations({
                        ...filters,
                        page: pagination.page + 1,
                        limit: PAGE_SIZE
                      }));
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkAuthListPage;